import axios, { AxiosInstance } from 'axios';
import { logErrorAndReport } from './errorReporting';
import { getAuth } from './storage';

type RequestOptions = {
  authAs?: 'payload' | 'params' | 'header' | 'none';
  token?: string;
};

/**
 * Determines if an error is an Axios network error (connectivity issue)
 * rather than an HTTP error response (4xx, 5xx, etc.)
 */
export function isAxiosNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  // Check if it's an Axios error
  if (!('isAxiosError' in error) || !error.isAxiosError) {
    return false;
  }

  const axiosError = error as { code?: string; message?: string; response?: unknown };

  // If there's a response, it's an HTTP error, not a network error
  if (axiosError.response) {
    return false;
  }

  // Check for network error codes
  const networkErrorCodes = ['ERR_NETWORK', 'ECONNABORTED'];
  if (axiosError.code && networkErrorCodes.includes(axiosError.code)) {
    return true;
  }

  // Check for network error in message
  if (axiosError.message?.toLowerCase().includes('network error')) {
    return true;
  }

  return false;
}

export default abstract class HttpRequest {
  protected readonly axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({ baseURL });
  }

  protected async get<T>({
    url,
    params,
    options = { authAs: 'none' },
  }: {
    url: string;
    params?: any;
    options?: RequestOptions;
  }): Promise<T | null> {
    const { authAs, token } = options;

    const auth = getAuth();

    if (authAs !== 'none' && !token && !auth) {
      const error = new Error(
        `There is no authentication token. Could not process [GET] authenticated request to ${url}`
      );
      logErrorAndReport({ error });
      return null;
    }

    try {
      return (
        await this.axiosInstance.get<T>(url, {
          params: {
            ...(authAs === 'params' ? { auth_token: token ?? auth!.token } : {}),
            ...(params || {}),
          },
          ...(authAs === 'header'
            ? { headers: { Authorization: `Bearer ${token ?? auth!.token}` } }
            : {}),
        })
      ).data;
    } catch (error) {
      logErrorAndReport({ error });
    }

    return null;
  }

  protected async post<T>({
    url,
    payload,
    options = { authAs: 'none' },
  }: {
    url: string;
    payload?: any;
    options?: RequestOptions;
  }): Promise<T | null> {
    const { authAs, token } = options;

    const auth = getAuth();

    if (authAs !== 'none' && !token && !auth) {
      console.error({
        error: `There is no authentication token. Could not process [POST] authenticated request to ${url}`,
      });

      return null;
    }

    try {
      return (
        await this.axiosInstance.post(
          url,
          {
            ...(payload || {}),
            ...(authAs === 'payload' ? { auth_token: token ?? auth!.token } : {}),
          },
          {
            ...(authAs === 'header'
              ? { headers: { Authorization: `Bearer ${token ?? auth!.token}` } }
              : authAs === 'params'
              ? { params: { auth_token: auth!.token } }
              : {}),
          }
        )
      ).data;
    } catch (error) {
      logErrorAndReport({ error });
    }

    return null;
  }

  protected async put<T>({
    url,
    payload,
    params,
    options = { authAs: 'none' },
  }: {
    url: string;
    payload?: any;
    params?: any;
    options?: RequestOptions;
  }): Promise<T | null> {
    const { authAs, token } = options;

    const auth = getAuth();

    if (authAs !== 'none' && !token && !auth) {
      console.error({
        error: `There is no authentication token. Could not process [PUT] authenticated request to ${url}`,
      });

      return null;
    }

    try {
      return (
        await this.axiosInstance.put(
          url,
          {
            ...(payload || {}),
            ...(authAs === 'payload' ? { auth_token: token ?? auth!.token } : {}),
          },
          {
            ...(authAs === 'header'
              ? { headers: { Authorization: `Bearer ${token ?? auth!.token}` } }
              : {}),
            params,
          }
        )
      ).data;
    } catch (error) {
      logErrorAndReport({ error });
    }

    return null;
  }

  protected async patch<T>({
    url,
    payload,
    params,
    options = { authAs: 'none' },
  }: {
    url: string;
    payload?: any;
    params?: any;
    options?: RequestOptions;
  }): Promise<T | null> {
    const { authAs, token } = options;

    const auth = getAuth();

    if (authAs !== 'none' && !token && !auth) {
      console.error({
        error: `There is no authentication token. Could not process [PATCH] authenticated request to ${url}`,
      });

      return null;
    }

    try {
      return (
        await this.axiosInstance.patch<T>(
          url,
          {
            ...(payload || {}),
            ...(authAs === 'payload' ? { auth_token: token ?? auth!.token } : {}),
          },
          {
            ...(authAs === 'header'
              ? { headers: { Authorization: `Bearer ${token ?? auth!.token}` } }
              : {}),
            params,
          }
        )
      ).data;
    } catch (error) {
      logErrorAndReport({ error });
    }

    return null;
  }

  protected async delete<T>({
    url,
    payload,
    options = { authAs: 'none' },
  }: {
    url: string;
    payload?: any;
    options?: RequestOptions;
  }): Promise<T | null> {
    const { authAs, token } = options;

    const auth = getAuth();

    if (authAs !== 'none' && !token && !auth) {
      console.error({
        error: `There is no authentication token. Could not process [DELETE] authenticated request to ${url}`,
      });

      return null;
    }

    try {
      return (
        await this.axiosInstance.delete(url, {
          ...(payload || {}),
          ...(authAs === 'header'
            ? { headers: { Authorization: `Bearer ${token ?? auth!.token}` } }
            : {}),
        })
      ).data;
    } catch (error) {
      logErrorAndReport({ error });
    }

    return null;
  }
}
